import { cn } from "@/lib/cn";
import { EditorContent, Node as TiptapNode, useEditor } from "@tiptap/react";
import { useEffect, useRef, useState } from "react";
import { emptyHtml, templateToTokens, tokensToTemplate } from "@/lib/format";
import { labelFor, PlaceholderToken, type PlaceholderField } from "@/components/editor/HtmlEditor";

type SubjectFieldProps = {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  "aria-label"?: string;
  /** Current placeholder values; chips show these instead of `{{key}}`. */
  values?: Record<string, string>;
  fields?: PlaceholderField[];
  onPickPlaceholder?: (name: string) => void;
};

/** Doc content is inline directly — no paragraph wrapper, so it stays a single line. */
const SingleLineDocument = TiptapNode.create({
  name: "doc",
  topNode: true,
  content: "inline*",
});

const TextNode = TiptapNode.create({
  name: "text",
  group: "inline",
});

const frame =
  "relative min-w-[10rem] flex-1 rounded-md border border-field-border bg-surface px-2 py-1 transition-colors focus-within:border-accent";

/**
 * Single-line counterpart to `HtmlEditor`: same placeholder chips, but plain
 * text (no formatting, no line breaks) for use in a subject line.
 */
export function SubjectField({
  value,
  onChange,
  disabled,
  placeholder,
  className,
  "aria-label": ariaLabel,
  values,
  fields,
  onPickPlaceholder,
}: SubjectFieldProps) {
  const phValues = values ?? {};
  const phFields = fields ?? [];
  const renderRef = useRef<(name: string) => { label: string; filled: boolean }>(() => ({
    label: "",
    filled: false,
  }));
  renderRef.current = (name: string) => labelFor(name, phValues, phFields);
  const pickRef = useRef(onPickPlaceholder);
  pickRef.current = onPickPlaceholder;
  const [empty, setEmpty] = useState(!value.trim());

  const editor = useEditor({
    extensions: [SingleLineDocument, TextNode, PlaceholderToken],
    content: templateToTokens(value || "", (name) => renderRef.current(name)),
    editable: !disabled,
    editorProps: {
      attributes: {
        class: "subject-chip-field",
        ...(ariaLabel ? { "aria-label": ariaLabel } : {}),
      },
      handleKeyDown: (_view, event) => event.key === "Enter",
      handlePaste: (view, event) => {
        const text = event.clipboardData?.getData("text/plain") ?? "";
        if (!text) return false;
        view.dispatch(view.state.tr.insertText(text.replace(/\r?\n/g, " ")));
        return true;
      },
      handleClickOn: (_view, _pos, node) => {
        if (node.type.name !== "placeholderToken") return false;
        const name = String(node.attrs.name || "");
        if (!name || !pickRef.current) return false;
        pickRef.current(name);
        return true;
      },
    },
    onUpdate: ({ editor: instance }) => {
      setEmpty(instance.isEmpty);
      onChange(tokensToTemplate(instance.getHTML()));
    },
  });

  useEffect(() => {
    if (!editor) return;
    const current = tokensToTemplate(editor.getHTML());
    if (emptyHtml(value) && emptyHtml(current)) return;
    if (value !== current) {
      editor.commands.setContent(
        templateToTokens(value || "", (name) => renderRef.current(name)),
        false,
      );
    }
    setEmpty(editor.isEmpty);
  }, [value, editor]);

  // Values changed elsewhere (the Eckdaten rail): relabel the chips in place.
  useEffect(() => {
    if (!editor) return;
    const updates: { pos: number; label: string; filled: boolean }[] = [];
    editor.state.doc.descendants((node, pos) => {
      if (node.type.name !== "placeholderToken") return;
      const next = labelFor(String(node.attrs.name || ""), phValues, phFields);
      if (node.attrs.label !== next.label || node.attrs.filled !== next.filled) {
        updates.push({ pos, ...next });
      }
    });
    if (!updates.length) return;
    const { tr } = editor.state;
    for (const update of updates) {
      const node = editor.state.doc.nodeAt(update.pos);
      if (!node) continue;
      tr.setNodeMarkup(update.pos, undefined, { ...node.attrs, label: update.label, filled: update.filled });
    }
    tr.setMeta("addToHistory", false);
    editor.view.dispatch(tr);
  }, [editor, values, fields]);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [disabled, editor]);

  if (!editor) {
    return <div className={cn(frame, className)} />;
  }

  return (
    <div className={cn(frame, disabled && "opacity-60", className)}>
      {empty && placeholder ? (
        <span className="pointer-events-none absolute inset-x-2 top-1 truncate text-muted">{placeholder}</span>
      ) : null}
      <EditorContent editor={editor} />
    </div>
  );
}
